import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getAdminBackupOverview,
  downloadAdminWorkspaceBackup,
  getMyProfile,
  previewAdminBackupRestore,
  restoreAdminBackup,
} from '../app/api';
import { useTranslation } from '../app/LanguageContext';

const AdminBackupPage = () => {
  const { translate } = useTranslation();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreWorkspaceName, setRestoreWorkspaceName] = useState('');
  const [restoreUserId, setRestoreUserId] = useState('');
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  const [restorePreviewLoading, setRestorePreviewLoading] = useState(false);

  const loadOverview = async () => {
    const overview = await getAdminBackupOverview();
    setUsers(overview.data?.users || []);
    setWorkspaces(overview.data?.workspaces || []);
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const me = await getMyProfile();
        if (cancelled) return;
        const p = me.data?.profile;
        setProfile(p || '');
        if (p !== 'ADMIN') {
          setLoading(false);
          return;
        }
        await loadOverview();
      } catch (e) {
        if (!cancelled) {
          setSnackbar({ open: true, message: e.message, severity: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = async (workspaceId) => {
    try {
      setDownloadingId(workspaceId);
      await downloadAdminWorkspaceBackup(workspaceId);
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRestoreFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      setRestorePreviewLoading(true);
      const res = await previewAdminBackupRestore(f);
      setRestoreFile(f);
      setRestoreWorkspaceName(res.data?.suggested_workspace_name || '');
      setRestoreUserId('');
      setRestoreDialogOpen(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || translate('admin_backup.restore_preview_error'),
        severity: 'error',
      });
    } finally {
      setRestorePreviewLoading(false);
    }
  };

  const closeRestoreDialog = () => {
    setRestoreDialogOpen(false);
    setRestoreFile(null);
    setRestoreWorkspaceName('');
    setRestoreUserId('');
  };

  const handleRestoreSubmit = async () => {
    if (!restoreFile || !restoreUserId.trim()) {
      setSnackbar({ open: true, message: translate('admin_backup.restore_link_user'), severity: 'error' });
      return;
    }
    try {
      setRestoreSubmitting(true);
      await restoreAdminBackup(restoreFile, restoreWorkspaceName, restoreUserId);
      setSnackbar({
        open: true,
        message: translate('admin_backup.restore_success'),
        severity: 'success',
      });
      closeRestoreDialog();
      await loadOverview();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setRestoreSubmitting(false);
    }
  };

  if (!loading && profile && profile !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return (
    <Box>
      {loading ? (
        <Typography color="text.secondary">{translate('common.loading')}</Typography>
      ) : profile === 'ADMIN' ? (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            {translate('admin_backup.restore_section')}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              style={{ display: 'none' }}
              onChange={handleRestoreFileChange}
            />
            <Button
              variant="outlined"
              disabled={restorePreviewLoading}
              onClick={() => fileInputRef.current?.click()}
            >
              {restorePreviewLoading
                ? translate('common.loading')
                : translate('admin_backup.restore_pick_file')}
            </Button>
          </Box>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            {translate('admin_backup.users_title')}
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3, width: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('users.email')}</TableCell>
                  <TableCell>{translate('users.status')}</TableCell>
                  <TableCell>{translate('users.profile')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: { xs: 160, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{u.status}</TableCell>
                    <TableCell>{u.profile}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {translate('admin_backup.workspaces_title')}
          </Typography>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('workspace.name')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{translate('admin_backup.members')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workspaces.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{w.name}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ maxWidth: { xs: 160, sm: 240 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {w.id}
                      </Typography>
                      {w.members && w.members.length > 0 && (
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                          {w.members.map((m) => (
                            <Typography key={`${w.id}-${m.user_id}-xs`} variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                              {m.email} ({m.role})
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {w.members && w.members.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {w.members.map((m) => (
                            <Chip
                              key={`${w.id}-${m.user_id}`}
                              size="small"
                              label={`${m.email} (${m.role})`}
                              variant="outlined"
                              sx={{ maxWidth: 280, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {translate('admin_backup.no_members')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={downloadingId === w.id}
                        onClick={() => handleDownload(w.id)}
                      >
                        {downloadingId === w.id
                          ? translate('admin_backup.downloading')
                          : translate('admin_backup.download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}

      <Dialog open={restoreDialogOpen} onClose={closeRestoreDialog} fullWidth maxWidth="sm">
        <DialogTitle>{translate('admin_backup.restore_dialog_title')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label={translate('admin_backup.restore_workspace_name')}
            value={restoreWorkspaceName}
            onChange={(ev) => setRestoreWorkspaceName(ev.target.value)}
          />
          <FormControl margin="normal" fullWidth>
            <InputLabel id="restore-user-label">{translate('admin_backup.restore_link_user')}</InputLabel>
            <Select
              labelId="restore-user-label"
              label={translate('admin_backup.restore_link_user')}
              value={restoreUserId}
              onChange={(ev) => setRestoreUserId(ev.target.value)}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.email} ({u.profile})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRestoreDialog}>{translate('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={restoreSubmitting || !restoreFile}
            onClick={handleRestoreSubmit}
          >
            {restoreSubmitting
              ? translate('admin_backup.restore_in_progress')
              : translate('admin_backup.restore_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminBackupPage;
